import { Flex, Switch, Text } from '@chakra-ui/react';

import {
  accent,
  controlHeight,
  font,
  fontSize,
  neutral,
  primaryAction,
  radius,
  text as textToken,
} from 'styles/revampTokens';

import FigmaScenarioBranchIcon from '../icons/FigmaScenarioBranchIcon';
import FigmaBlockDimensionsIcon from '../icons/FigmaBlockDimensionsIcon';
import FigmaSmallChevronIcon from '../icons/FigmaSmallChevronIcon';

import type React from 'react';

interface Properties {
  title: string;
  scenarioName: string;
  onScenarioClick?: () => void;
  /** Rendered in place of the static pill — the live scenario dropdown. */
  scenarioControl?: React.ReactNode;
  selectedDimensionCount: number;
  onDimensionsClick?: () => void;
  /** Rendered in place of the static pill — the live dimensions dropdown. */
  dimensionsControl?: React.ReactNode;
  isBuilderModeOn: boolean;
  onBuilderModeToggle: (checked: boolean) => void;
}

// Title + scenario/dimensions pills + Builder mode toggle. Padding (12px,
// not the Figma artboard's 72px) and title size (20px/600, not 24px/bold)
// match PlanRevamp's `PlanPageTitleBar` exactly — that component stepped
// down from the raw Figma spec to line up with DimensionsRevamp's header,
// and this one follows the same corrected values rather than the artboard.
const BuilderModeHeader = ({
  title,
  scenarioName,
  onScenarioClick,
  scenarioControl,
  selectedDimensionCount,
  onDimensionsClick,
  dimensionsControl,
  isBuilderModeOn,
  onBuilderModeToggle,
}: Properties) => {
  return (
    <Flex
      as='header'
      bg={neutral.surface}
      borderBottom='1px solid'
      borderColor={neutral.border}
      alignItems='center'
      justifyContent='space-between'
      px='12px'
      py='12px'
    >
      <Flex alignItems='center' gap='32px'>
        <Text fontFamily={font.display} fontWeight={600} fontSize='20px' color={primaryAction.bg}>
          {title}
        </Text>

        <Flex alignItems='center' gap='16px'>
          {scenarioControl ?? (
            <Flex
              as='button'
              type='button'
              alignItems='center'
              gap='8px'
              bg={neutral.surface}
              border='1px solid'
              borderColor={neutral.border}
              borderRadius={radius.control}
              h={controlHeight}
              px='12px'
              w='151px'
              cursor='pointer'
              onClick={onScenarioClick}
            >
              <FigmaScenarioBranchIcon color={accent.solid} />
              <Text
                flex='1'
                textAlign='left'
                fontFamily={font.body}
                fontSize={fontSize.body}
                fontWeight={500}
                color={primaryAction.bg}
                noOfLines={1}
              >
                {scenarioName}
              </Text>
              <FigmaSmallChevronIcon rotate={270} color={primaryAction.bg} />
            </Flex>
          )}

          {dimensionsControl ?? (
            <Flex
              as='button'
              type='button'
              alignItems='center'
              gap='8px'
              bg={neutral.surface}
              border='1px solid'
              borderColor={neutral.border}
              borderRadius={radius.control}
              h={controlHeight}
              px='12px'
              cursor='pointer'
              onClick={onDimensionsClick}
            >
              <FigmaBlockDimensionsIcon color={accent.solid} />
              <Text
                fontFamily={font.body}
                fontSize={fontSize.body}
                fontWeight={500}
                color={primaryAction.bg}
                whiteSpace='nowrap'
              >
                Block Dimensions
              </Text>
              {selectedDimensionCount > 0 && (
                <>
                  <Flex w='1px' h='17px' bg={neutral.border} />
                  <Text
                    fontFamily={font.body}
                    fontWeight={600}
                    fontSize={fontSize.body}
                    color={accent.solid}
                    whiteSpace='nowrap'
                  >
                    ({selectedDimensionCount} selected)
                  </Text>
                  <FigmaSmallChevronIcon color={primaryAction.bg} />
                </>
              )}
            </Flex>
          )}
        </Flex>
      </Flex>

      {/* Compact switch geometry matches PlanBuilderModeToggle exactly — same
          22x12 track override — so the two Builder mode pills read as one
          control across modules instead of Plan's being 4px shorter. */}
      <Flex
        as='label'
        alignItems='center'
        gap='8px'
        bg={primaryAction.bg}
        borderRadius={radius.control}
        px='12px'
        h={controlHeight}
        cursor='pointer'
      >
        <Text
          fontFamily={font.body}
          fontSize={fontSize.body}
          fontWeight={500}
          color={textToken.inverse}
        >
          Builder mode
        </Text>
        <Switch
          size='sm'
          isChecked={isBuilderModeOn}
          onChange={(event) => onBuilderModeToggle(event.target.checked)}
          sx={{
            '--switch-track-width': '22px',
            '--switch-track-height': '12px',
            '--switch-track-padding': '1px',
            '& .chakra-switch__track': {
              bg: textToken.inverse,
              _checked: { bg: textToken.inverse },
            },
            '& .chakra-switch__thumb': {
              bg: isBuilderModeOn ? accent.solid : primaryAction.bg,
              boxShadow: '0px 1.459px 3.405px rgba(0, 0, 0, 0.12)',
            },
          }}
        />
      </Flex>
    </Flex>
  );
};

export default BuilderModeHeader;
